import React, { useMemo, useRef, useState } from "react";
import moment from "moment";
import { useQuery, useQueryClient } from "react-query";
import { apiGetList, apiPost, apiDelete, getErrorMessage } from "services/api";
import { useOperationTypes } from "shared-apis";
import { DocumentsOperations } from "constant-data/documentsData";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText, RegularText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { SearchInput } from "components/SearchInput";
import { SimpleList, SimpleListRow } from "components/SimpleList";
import { VerticalSpace } from "components/Spacing";
import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
  ConfirmationModal,
  InfoModal,
} from "components/Modal";

import styles from "./styles.module.scss";

const TEMPLATES_KEY = "print-templates-docs";
const FILES_HOST = "https://florapoint.su";

const getTemplates = (docOperation) =>
  apiGetList("documents/templates", {
    page: 1,
    pageSize: 1000,
    doc_operation: docOperation ?? null,
  });

/** Абсолютный URL файла шаблона (старые записи с florapoint.tech тоже работают) */
const resolveTemplateFileUrl = (filePath) => {
  if (!filePath) return "";
  if (/^https?:\/\//i.test(filePath)) {
    return filePath.replace("https://florapoint.tech", FILES_HOST);
  }
  return `${FILES_HOST}${filePath.startsWith("/") ? "" : "/"}${filePath}`;
};

/* ---------- Модалка добавления шаблона ---------- */

const AddTemplateModal = ({ typesOptions, initialTypeOption = null }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const [title, setTitle] = useState("");
  const [typeOption, setTypeOption] = useState(initialTypeOption);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleSave = async () => {
    if (!title.trim() || !typeOption || !file) {
      setError("Заполните название, тип документа и выберите файл");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file, file.name);
      const uploaded = await apiPost("documents/template/file", formData);
      const filePath =
        typeof uploaded === "string"
          ? uploaded
          : uploaded?.path || uploaded?.file;

      if (!filePath) {
        setError("Не удалось загрузить файл");
        return;
      }

      await apiPost("documents/template", {
        title: title.trim(),
        doc_operation: typeOption.value,
        file_path: filePath,
      });

      queryClient.invalidateQueries([TEMPLATES_KEY]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell width={520}>
      <ModalHeader title="Новый шаблон для печати" onClose={closeModal} />
      <ModalBody>
        <VerticalSpace size={8} />
        <Input
          label="Название"
          labelWidth={130}
          value={title}
          onChange={(ev) => setTitle(ev.target.value)}
          inputWidth={280}
        />
        <VerticalSpace size={12} />
        <Select
          label="Тип документа"
          labelWidth={130}
          options={typesOptions}
          value={typeOption}
          onChange={setTypeOption}
          width={280}
        />
        <VerticalSpace size={16} />
        <div className={styles.fileRow}>
          <Button onClick={() => fileInputRef.current?.click()}>
            {file ? "Выбрать другой файл" : "Выбрать файл"}
          </Button>
          {!!file && (
            <RegularText size={13} color="muted" className={styles.fileName}>
              {file.name}
            </RegularText>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          hidden
          onChange={(ev) => {
            setFile(ev.target.files?.[0] ?? null);
            ev.target.value = "";
          }}
        />
        {!!error && (
          <>
            <VerticalSpace size={12} />
            <RegularText size={13} color="danger">
              {error}
            </RegularText>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          loading={saving}
        >
          Сохранить
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};

/* ---------- Страница ---------- */

export const PrintTemplatesPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const typesQuery = useOperationTypes();
  const types = typesQuery.data?.items ?? [];

  const typesOptions = useMemo(
    () => types.map((t) => ({ value: String(t.operationid), label: t.name })),
    [types]
  );

  const [activeType, setActiveType] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [search, setSearch] = useState("");

  const templatesQuery = useQuery(
    [TEMPLATES_KEY, activeType?.operationid ?? null],
    () => getTemplates(activeType?.operationid ?? null)
  );
  const templates = useMemo(
    () => templatesQuery.data?.items ?? [],
    [templatesQuery.data]
  );

  const filtered = useMemo(() => {
    if (!search) return templates;
    return templates.filter((t) =>
      t.title?.toLowerCase().includes(search.toLowerCase())
    );
  }, [templates, search]);

  const rowData = useMemo(
    () =>
      filtered.map((t) => ({
        template_id: t.template_id,
        title: t.title,
        createdAt: t.createdAt ? moment(t.createdAt).format("DD.MM.YYYY") : "",
        typeName:
          DocumentsOperations.find(
            (op) => String(op.id) === String(t.doc_operation)
          )?.name ?? String(t.doc_operation),
      })),
    [filtered]
  );

  const colDefs = useMemo(
    () => [
      { field: "title", headerName: "Название", flex: 2 },
      { field: "createdAt", headerName: "Дата создания", flex: 1 },
      { field: "typeName", headerName: "Тип документа", flex: 1 },
    ],
    []
  );

  const downloadTemplate = (template) => {
    openModal(
      <ConfirmationModal
        title="Вы хотите скачать файл этого шаблона?"
        confirmText="Скачать"
        danger={false}
        onConfirm={() => {
          const link = document.createElement("a");
          link.href = resolveTemplateFileUrl(template.file_path);
          link.download = `florapoint_document_sheet-${new Date().toISOString()}.xlsx`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          closeModal();
        }}
        onClose={closeModal}
      />
    );
  };

  const onDeleteClick = () => {
    if (!selectedTemplate) return;
    openModal(
      <ConfirmationModal
        title="Вы действительно хотите удалить этот шаблон для печати?"
        onConfirm={async () => {
          try {
            await apiDelete("documents/template", {
              template_id: Number(selectedTemplate.template_id),
            });
            setSelectedTemplate(null);
            queryClient.invalidateQueries([TEMPLATES_KEY]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <div className={styles.page}>
      <PageLayout
        className={styles.leftPanel}
        header={<BoldText>Типы документов</BoldText>}
      >
        <SimpleList
          items={types}
          rowKey="operationid"
          activeId={activeType?.operationid}
          loading={typesQuery.isLoading}
          error={typesQuery.isError ? getErrorMessage(typesQuery.error) : ""}
          onRetry={typesQuery.refetch}
          topRow={
            <SimpleListRow
              label="Все"
              isActive={!activeType}
              onClick={() => setActiveType(null)}
            />
          }
          onRowClick={setActiveType}
        />
      </PageLayout>

      <PageLayout
        className={styles.rightPanel}
        header={<BoldText>Шаблоны для печати документов</BoldText>}
        footer={
          <ActionsRow>
            <Button
              icon="plus"
              onClick={() =>
                openModal(
                  <AddTemplateModal
                    typesOptions={typesOptions}
                    initialTypeOption={
                      activeType
                        ? typesOptions.find(
                            (o) =>
                              String(o.value) === String(activeType.operationid)
                          ) || null
                        : null
                    }
                  />
                )
              }
            >
              Добавить шаблон
            </Button>
            <Button
              icon="download"
              onClick={() => downloadTemplate(selectedTemplate)}
              disabled={!selectedTemplate}
            >
              Скачать файл
            </Button>
            <Button
              icon="delete"
              onClick={onDeleteClick}
              disabled={!selectedTemplate}
              title="Удалить шаблон"
            />
          </ActionsRow>
        }
        bodyFill
      >
        <div className={styles.searchRow}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Поиск по названию"
          />
        </div>
        <Table
          fill
          rowData={rowData}
          colDefs={colDefs}
          rowKey="template_id"
          selectedId={selectedTemplate?.template_id}
          onSelect={(row) =>
            setSelectedTemplate(
              templates.find((t) => t.template_id === row.template_id) || null
            )
          }
          onOpen={(row) => {
            const template = templates.find(
              (t) => t.template_id === row.template_id
            );
            if (template) downloadTemplate(template);
          }}
          loading={templatesQuery.isLoading}
          error={
            templatesQuery.isError ? getErrorMessage(templatesQuery.error) : ""
          }
          onRetry={templatesQuery.refetch}
        />
      </PageLayout>
    </div>
  );
};
